import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Box, Button, Group, Modal, Text, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import type {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import type {ListableItem, ListableItemExtras} from 'search/SearchConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';

import {type FetchedEntityComponents, fetchEntityComponents} from './fetchEntityComponents';

const TEST_COMPONENT = 'EntitySearchModal';

// Where the picked entities land: each becomes its own input, or they all join one input.
export type EntitySearchDestination = 'inputs' | 'components';

interface EntitySearchModalProps {
  destination: EntitySearchDestination;
  // How many more entities the destination can take.
  maxEntities: number;
  moleculesSupported: boolean;
  onAdd: (result: FetchedEntityComponents) => void;
  onClose: () => void;
}

const COPY: Record<EntitySearchDestination, {title: string; unit: string}> = {
  inputs: {title: 'Add inputs', unit: 'input'},
  components: {title: 'Add components', unit: 'component'},
};

function isUnpredictableAaSequence(item: ListableItem): boolean {
  return (
    APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROTEIN &&
    (item.permissions as ListableItemExtras['permissions'] & {structurePredictionEditable?: boolean})
      .structurePredictionEditable === false
  );
}

function getInvalidItemTooltipText(item: ListableItem): string | undefined {
  return isUnpredictableAaSequence(item)
    ? "You don't have permission to run predictions on this AA sequence"
    : undefined;
}

/** Picks registry entities for New prediction and loads what each contributes as a component. */
export default function EntitySearchModal({
  destination,
  maxEntities,
  moleculesSupported,
  onAdd,
  onClose,
}: EntitySearchModalProps): JSX.Element {
  const {classes} = useStyles();
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {title, unit} = COPY[destination];

  const availableTypes = useMemo(
    (): Array<OligoAwareFolderItemType> =>
      BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && moleculesSupported
        ? [ModelTypeConstants.PROTEIN, ModelTypeConstants.MOLECULE]
        : [ModelTypeConstants.PROTEIN],
    [moleculesSupported]
  );

  // The search view keeps its query and filters in the store per nav section, so without this it
  // would reopen on whatever the last Model Hub search left behind, including other entity types.
  useEffect(() => {
    clearBioentitySearchViewSearchParams(availableTypes, Section.MODEL_HUB_STRUCTURE_PREDICTION);
  }, [availableTypes]);

  const isOverLimit = selectedIds.length > maxEntities;

  // The picker can be dismissed while a load is in flight; its result then belongs to no picker, so
  // it's dropped rather than added.
  const isOpenRef = useRef(true);
  useEffect(() => {
    isOpenRef.current = true;
    return () => {
      isOpenRef.current = false;
    };
  }, []);

  const handleAdd = useCallback((): void => {
    setIsAdding(true);
    setError(null);
    // Stays open on failure so the selection isn't lost.
    fetchEntityComponents(selectedIds).then(
      (result) => {
        if (isOpenRef.current) {
          onAdd(result);
        }
      },
      () => {
        setError("The selected entities couldn't be loaded. Try again.");
        setIsAdding(false);
      }
    );
  }, [onAdd, selectedIds]);

  return (
    // A px string: Mantine turns a numeric size into rem, which the monolith's 62.5% root font
    // size shrinks to ~60%. The search header needs ~870px to keep its filters on one row.
    <Modal onClose={onClose} opened={true} size="min(1200px, 90vw)" title={title}>
      <Box className={classes.searchView} {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
        <BioentitySearchView
          availableTypes={availableTypes}
          getInvalidItemTooltipText={getInvalidItemTooltipText}
          isItemInvalid={isUnpredictableAaSequence}
          limitSelectedEntitiesParams={{
            maxNumEntitiesToAdd: maxEntities,
            selectedEntityName: pluralize(maxEntities, unit),
          }}
          navSection={Section.MODEL_HUB_STRUCTURE_PREDICTION}
          rowSelectionMode={RowSelectionMode.MULTIPLE}
          selectedBioentityIds={selectedIds}
          // The load in flight uses the selection from when Add was clicked, so it stays fixed until then.
          setSelectedBioentityIds={(ids: Array<string>) => {
            if (!isAdding) {
              setSelectedIds(ids);
            }
          }}
          showInvalid={true}
        />
      </Box>
      {error != null && (
        <Alert icon={<BenchlingIcon icon="exclamation-circle" />} mt="md" variant="error">
          {error}
        </Alert>
      )}
      <Group mt="md" noWrap={true} position="apart">
        <Text color="dimmed" size="sm">
          {selectedIds.length === 0
            ? 'Nothing selected yet'
            : `${selectedIds.length} ${pluralize(selectedIds.length, unit)} will be added`}
        </Text>
        <Group noWrap={true} spacing="sm">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button disabled={selectedIds.length === 0 || isOverLimit} loading={isAdding} onClick={handleAdd}>
            {selectedIds.length === 0 ? 'Add' : `Add ${selectedIds.length}`}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}

// Matches the theme's Modal body padding (nucleus-ui Modal.styles.ts).
const MODAL_BODY_PADDING = 16;

const useStyles = createStyles(() => ({
  // The search view fills its container and scrolls its own results, so it needs a bounded height;
  // molecule rows are ~200px tall, so leave room for several.
  searchView: {
    display: 'flex',
    flexDirection: 'column',
    height: 'min(640px, calc(100vh - 240px))',
    minHeight: 0,
    // The listing already insets its header, so cancel the modal body's padding around it.
    marginLeft: -MODAL_BODY_PADDING,
    marginRight: -MODAL_BODY_PADDING,
  },
}));
